import { ColDef, GridOptions, GridApi } from "ag-grid-community"; 
import { DatePipe } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, map, tap, switchMap } from 'rxjs/operators';
import { ApplicationsService } from '../../../../applications/services/applications.service';
import { ButtonRendererComponent } from '../../../../common/components/ag-grid/button-renderer.component';
import { AgGridBooleanFilterComponent, IBooleanFilterParams } from '../../../../common/components/ag-grid/filters/boolean-filter.component';
import { parseFileNameFromHeaders } from '../../../../common/models/onBaseIntegrationAndMapping.model';
import { OrganizationStub } from '../../../../common/models/organization.model';
import { DateService } from '../../../../common/services/date.service';
import { DocumentManagementService } from '../../../../common/services/document-management.service';
import { GridFormatterService } from '../../../../common/services/grid-formatter.service';
import { IssuesService } from '../../../../common/services/issues.service';
import { LoaderService } from '../../../../common/services/loader.service';
import { OrganizationService } from '../../../../common/services/organization.service';
import { WorkOrderService } from '../../../../common/services/work-order.service';
import { SubHeader } from '../../../../core/models/sub-header.model';
import { SubHeaderService } from '../../../../core/services/sub-header.service';
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import {ComplianceReportingType } from '../../../../issues/models/compliance.model';
import { ComplianceReport, ComplianceReportingSearchSpec } from '../../../../reports/model/compliance-reporting';
import { ReportsService } from '../../../../reports/reports.service';
import { ReportSearchCriteria } from '../../../models/admin-reports.model';
import { GroupService } from '../../../services/group.service';
import { CheckboxRendererComponent, CustomTextRenderOptions } from "../../../../common/components/ag-grid/checkbox-renderer.component";

@Component({
    selector: 'app-compliance-report',
    templateUrl: './compliance-report.component.html',
    styleUrls: ['./compliance-report.component.scss'],
    standalone: false
})
export class ComplianceReportComponent implements OnInit {

    allReports: ComplianceReport[];
    subscription: Subscription = new Subscription();
    organizations: OrganizationStub[];
    types: ComplianceReportingType[];
	tableGridOptions: GridOptions;
	tableGridApi?: GridApi;

    currentDate: Date = new Date();
    @ViewChild("downloadLink", { static: true }) public downloadLink: ElementRef;

    excelStyles: any = this.formatter.excelStyles; //anywhere we need to export apiNumber to excel we need the excelStyles



    get invalidDateCombination(): boolean {
        let invalid: boolean = false;
        if (this.toDate != null && this.fromDate != null) {
            invalid = this.toDate < this.fromDate;
        }
        return invalid;
    }

    toDate: Date | null;
    fromDate: Date | null

    subHeader: SubHeader = new SubHeader({
        title: "Compliance Reporting",
        buttons: []
    });

    approvedDateColumn: ColDef = {
        headerName: "Approved Date",
        headerTooltip: "Approved Date",
        field: "dateAccepted",
        tooltipField: "dateAccepted",
        valueGetter: this.formatter.formatDate("dateAccepted"),
        tooltipValueGetter: this.formatter.formatDate("dateAccepted"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 150

    } as ColDef;

    apiNumberColumn: ColDef = {
        headerName: "API #",
        headerTooltip: "API #",
        field: "apiNumber",
        tooltipField: "apiNumber",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        cellClass: "stringType",
        minWidth: 150

    } as ColDef;

    applicationNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Application #",
        headerTooltip: "Application #",
        field: "applicationNumber",
        tooltipField: "applicationNumber",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 160
    } as ColDef;

    organizationColumn: ColDef = {
        headerName: "Organization",
        headerTooltip: "Organization",
        field: "organizationName",
        valueFormatter: this.formatOrganization("organizationName"),
        tooltipValueGetter: this.formatOrganization("organizationName"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150

    } as ColDef;


    applicationStatusColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Application Status",
        headerTooltip: "Application Status",
        field: "currentState",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150

    } as ColDef;

    reportTypeColumn: ColDef = {
        headerName: "Type of Report",
        headerTooltip: "Type of Report",
        field: "reportType",
        valueGetter: this.formatReportType("reportType"),
        tooltipValueGetter: this.formatReportType("reportType"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150

    } as ColDef;

    submittedColumn: ColDef = {
        headerName: "Submitted Date",
        headerTooltip: "Submitted Date",
        field: "dateSubmitted",
        tooltipField: "dateSubmitted",
        valueGetter: this.formatter.formatDate("dateSubmitted"),
        tooltipValueGetter: this.formatter.formatDate("dateSubmitted"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 150

    } as ColDef;

    wellNameColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Well Name",
        headerTooltip: "Well Name",
        field: "wellName",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        valueFormatter: this.formatter.formatPotentiallyBlankField("wellName", "N/A"),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("wellName", "N/A"),
        minWidth: 150

    } as ColDef;


    wellNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Well Number",
        headerTooltip: "Well Number",
        field: "wellNumber",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150

    } as ColDef;

    sourceColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Source",
        headerTooltip: "Source",
        field: "onBehalf",
        filter: AgGridBooleanFilterComponent,
        valueFormatter: this.formatter.formatSourceBoolean("onBehalf"),
        cellRenderer: CheckboxRendererComponent,
        cellRendererParams: {
            action: () => { return false; },
            field: "onBehalf",
            description: "Source",
            isVisible: () => { return true; },
            isEnabled: () => { return false; },
            renderAsYesNo: true,
            textRenderOptions: {
                trueOption: "Paper",
                falseOption: "RICS"
            } as CustomTextRenderOptions
        },
        filterParams: {
            isInServerSideFilterMode: false,
            trueValueDisplayFormat: "Paper",
            falseValueDisplayFormat: "RICS"
        } as IBooleanFilterParams,
        minWidth: 150

    }

    linkColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "View Documents",
        cellRenderer: ButtonRendererComponent,
        cellRendererParams: {
            buttonText: "View",
            action: (obj: any) => this.routeOrDownload(obj)
        },
        minWidth: 125,
        maxWidth: 125
    } as ColDef

    constructor(private issuesService: IssuesService, private formatter: GridFormatterService, private organizationService: OrganizationService, public appService: ApplicationsService,
        public groupService: GroupService, public workOrderService: WorkOrderService, public router: Router, private datePipe: DatePipe, private dialog: DynamicDialogService,
        private readonly dateService: DateService, public subHeaderService: SubHeaderService, public reportService: ReportsService, private _loader: LoaderService, private _docService: DocumentManagementService,
    ) {

    }

    ngOnInit() {
        
        this.subHeaderService.updateSubHeader(this.subHeader);

        this.fromDate = this.dateService.generateDateWithOffset(0, -1, 0);
        this.toDate = this.currentDate;

        this.generateReport();
        this.tableGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.buildColumns(), false);
		this.tableGridOptions.onGridReady = (params) => { this.tableGridApi = params.api; };
    }

    fromDateChanged(model: Date): void {
        this.fromDate = model;
    }

    toDateChanged(model: Date): void {
        this.toDate = model;
    }

   buildColumns(): ColDef[] {
        let colDefs: ColDef[] = [];
        colDefs.push(this.reportTypeColumn);
        colDefs.push(this.sourceColumn);
        colDefs.push(this.applicationStatusColumn); 
        colDefs.push(this.applicationNumberColumn);
        colDefs.push(this.organizationColumn);
        colDefs.push(this.wellNameColumn);
        colDefs.push(this.wellNumberColumn);
        colDefs.push(this.apiNumberColumn);
        colDefs.push(this.submittedColumn);
        colDefs.push(this.approvedDateColumn);
        colDefs.push(this.linkColumn);
        return colDefs;

    }

    formatReportType(field: string): any {
        return (obj: any) => {
            let returnValue = "N/A";
            if (obj && obj.data && obj.data[field] != null) {
                returnValue = obj.data[field];
                if (this.types != null) {
                    const type = this.types.find(o => o.typeId === returnValue);
                    if (type != null) returnValue = type.description;
                }
            }
            return returnValue;
        }
    }

    formatOrganization(field: string): any {
        return (obj: any) => {
            let returnValue = "N/A";
            if (obj && obj.data && obj.data[field] != null) {
                returnValue = obj.data[field];
                if (this.organizations != null && this.organizations.length > 0) {
                    let orgs = this.organizations.filter(o => o.id == returnValue);
                    if (orgs == null || orgs.length === 0) orgs = this.organizations.filter(o => o.historicalOwnerNumber === returnValue);
                    if (orgs != null) {
                        const registered = orgs.filter(o => o.hasRegistered === true);
                        if (registered != null && registered.length > 0) {
                            returnValue = registered[0].name;
                        } else {
                            returnValue = orgs[0].name;
                        }
                    }
                }
            }
            return returnValue;
        }
    }

    generateReport() {
       let type$: Observable<ComplianceReportingType[]> = (this.types == null || this.types.length == 0) ?
            this.issuesService.getComplianceReportingTypes().pipe(tap((types: ComplianceReportingType[]) => { this.types = types; })) : of(this.types);
        if (this.tableGridApi != null) {
            this.tableGridApi.setGridOption("loading", true);
        }
        var searchSpec = {
            endDate: this.toDate,
            startDate: this.fromDate
        } as ComplianceReportingSearchSpec

        let rObs$ = type$.pipe(
            switchMap(() => {
                return this.reportService.searchMetaDataAsComplianceReport(searchSpec).pipe(
                    map((meta: ComplianceReport[]) => {
                    this.allReports = meta;
            }),
            catchError((error) => {
                console.error(error);
                return of([]);
            }))
            })
        );
        
        

        let rSubs$ = rObs$.subscribe({
            next: () => {
             if (this.tableGridApi != null) {
            this.tableGridApi.setGridOption("loading", false);
        }
            },
            error: (error) => {
                console.error(error);
                if (this.tableGridApi != null) {
                    this.tableGridApi.setGridOption("loading", false);
                }
                this.dialog.showError();
            }
        });
        this.subscription.add(rSubs$)


    }

    routeOrDownload(obj: any): void {
        if (obj.dateAccepted != null && obj.identifier != null) {
            this.download(obj.identifier);

        }
        else {
            this.routeToApplication(obj.applicationNumber);
        }

    }

    download(identifier: string): void {
        this._loader.toggleLoader(true);
        let obs$: Observable<HttpResponse<Blob>> = this._docService.downloadFromQueue(identifier);

        const load$ = obs$.pipe()
            .subscribe({
                next:  (data: HttpResponse<Blob>) => {
                    this._loader.toggleLoader(false);
                    const fileName = parseFileNameFromHeaders(data.headers.get("content-disposition"));
                    const blob = new Blob([data.body], { type: data.body.type });
					const blobUri = window.URL.createObjectURL(blob);
					const anchor = <HTMLAnchorElement>this.downloadLink.nativeElement;
					anchor.href = blobUri;
					anchor.download = fileName;
					anchor.click();
                },
                error: (error) => {
                    console.error(error);
                    this.dialog.showError();
                }
            });
        this.subscription.add(load$);
    }

    downloadReport() {
        const criteria = new ReportSearchCriteria();
        criteria.fromDate = this.fromDate;
        criteria.toDate = this.toDate;
        let fileName = "ComplianceReport";
        if (this.fromDate && this.toDate) {
            const fromDate = this.datePipe.transform(this.fromDate, "yyyy-MM-dd");
            const toDate = this.datePipe.transform(this.toDate, "yyyy-MM-dd");
            fileName = `ComplianceReport_${fromDate}_${toDate}`;
        }

        const sheetName = "Compliance Report";
        this.formatter.exportDataToExcel(fileName, sheetName, this.tableGridApi);
    }

    routeToApplication(appNumber: string): void {
        this.router.navigate([`/issues/${appNumber}/review`]);

    }

}
